import Button from '@carvana/ds-button';
import { CircleAlert } from 'lucide-react';
import React, { useCallback, useContext, ErrorInfo } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { FormStateContext } from '|/context';
import styles from './something-went-wrong-modal.module.css';

interface SomethingWentWrongModalProps {
  isOpen: boolean;
  onClose: () => void;
  text?: string;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class SilentErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    void 0;
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

const SomethingWentWrongModalWrapper = ({
  isOpen,
  onClose,
  text
}: SomethingWentWrongModalProps) => {
  return (
    <SilentErrorBoundary>
      <SomethingWentWrongModalDisplay isOpen={isOpen} onClose={onClose} text={text} />
    </SilentErrorBoundary>
  );
};

const SomethingWentWrongModalWithContext = ({ text }: { text?: string }) => {
  const { submitError, setSubmitError } = useContext(FormStateContext);

  const handleClose = useCallback(() => {
    setSubmitError(null);
  }, [setSubmitError]);

  return (
    <SomethingWentWrongModalWrapper
      isOpen={submitError !== null}
      onClose={handleClose}
      text={text}
    />
  );
};

const SomethingWentWrongModalDisplay = ({
  isOpen,
  onClose,
  text
}: SomethingWentWrongModalProps) => {
  const { setSubmitError } = useContext(FormStateContext);

  const handleContinue = useCallback(() => {
    setSubmitError(null);
    onClose();
  }, [setSubmitError, onClose]);

  const handleCloseModal = useCallback(() => {
    setSubmitError(null);
    onClose();
  }, [setSubmitError, onClose]);

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.SomethingWentWrong}
      divLine={false}
      mobileStyle="inset"
      desktopWidth="480px"
      onClose={handleCloseModal}
    >
      <div className={styles.contentLockup}>
        <div className={styles.header}>
          <div className={styles.heading}>
            <div className={styles.iconArea}>
              <CircleAlert size={24} className={styles.icon} />
            </div>
            <div className={styles.headline}>There was an issue</div>
          </div>
          <div className={styles.body}>
            {text
              ? `There was an issue ${text}. Try again later.`
              : 'There was an issue. Try again later.'}
          </div>
        </div>
        <Button onClick={handleContinue} data-analytics-id="something-went-wrong-ok">
          Ok
        </Button>
      </div>
    </EventedModal>
  );
};

const SomethingWentWrongModal = SomethingWentWrongModalWithContext;

export default SomethingWentWrongModal;
export { SomethingWentWrongModalWrapper };
